import React, { useContext, useState, useEffect } from 'react'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { selectData } from 'layouts/onboarding'
import ValuePoint from 'admin/components/ValuePoint'

import { Card, Grid, Typography, Button, Collapse } from '@mui/material'
import { ExpandMore } from '@mui/icons-material'

import { differenceInYears, differenceInMonths, addMonths } from 'date-fns'

const dateToYears = (startDate, endDate) => {
  const years =
    startDate && endDate
      ? differenceInYears(new Date(endDate), new Date(startDate))
      : ''
  const diff =
    startDate && endDate
      ? differenceInMonths(new Date(endDate), new Date(startDate))
      : ''
  const months =
    startDate && endDate
      ? differenceInMonths(
          new Date(endDate),
          addMonths(new Date(startDate), years * 12)
        )
      : ''
  return { years, months }
}

const dollarCentsFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

function Healthcare({
  collapseSections,
  toggleCollapse,
  intakeState,
  updateIntakeStateAtKey,
  updateIntakeStateAndSubmit,
}) {
  const isGovernmentEmployee = intakeState?.governmentEmployee
  const [localForm, setLocalForm] = useState({
    healthInsurancePremium: intakeState.healthInsurancePremium || '',
    healthInsurancePremiumRetirement:
      intakeState.healthInsurancePremiumRetirement || '',
  })
  const handleChange = (field) => (value) => {
    setLocalForm((prev) => ({ ...prev, [field]: value }))
  }

  const fehbLookup = intakeState?.fehbCode
    ? intakeState.fehbCode.replace('HP ', '').match(/^\d/)
      ? '_' + intakeState.fehbCode.replace('HP ', '')
      : intakeState.fehbCode
    : ''
  const suggestedPremium = selectData.fehb[fehbLookup]

  function fehbReimbursement(status, dependents, planType) {
    const isNotMarried = status !== 'married'
    const fehbPlan = planType !== 'Self'
    const noQualifyingKids =
      dependents.length === 0 || dependents.every((dep) => dep.age >= 26)
    return isNotMarried && noQualifyingKids && fehbPlan
  }

  const healthInsurance = intakeState?.healthInsurance
  const maritalStatus = intakeState?.maritalStatus

  const yearsUntil26 = intakeState?.familyDependents?.length
    ? 26 - Math.min(...intakeState.familyDependents.map((d) => Number(d.age)))
    : undefined
  const undefinedTest = undefined

  useEffect(() => {
    if (!intakeState.fehbCode) return
    const premium = suggestedPremium?.biweeklyEmp || 0

    if (premium) {
      updateIntakeStateAndSubmit(() =>
        updateIntakeStateAtKey({ healthInsurancePremium: premium })
      )()
      handleChange('healthInsurancePremium')(premium)
    }
  }, [intakeState?.fehbCode, suggestedPremium])

  return (
    <>
      <Grid item xs={12} md={4} xl={3}>
        <Card
          sx={{
            overflow: 'visible',
            textAlign: 'left',
            boxShadow: '0px 4px 25px -8px rgba(0,0,0,0.2)',
            backgroundColor: '#f8f9fa',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Grid container columnSpacing={3} rowSpacing={2}>
            <Grid item xs={12}>
              <Typography variant="valueTitle">VALUE POINTS</Typography>
              <Collapse
                in={collapseSections.healthcare}
                style={{ width: '100%' }}
              >
                {intakeState?.fehbFivePlus === false ? (
                  <ValuePoint text="Does NOT meet 5 year rule" type="error" />
                ) : (
                  <ValuePoint text="Meets 5 Year Rule" type="success" />
                )}
                {dateToYears(
                  intakeState.dateOfBirth,
                  intakeState.dateOfPlannedRetirement
                ).years >= 65 ? (
                  <>
                    <ValuePoint
                      text="Enroll in Medicare Part A + B during initial enrollment window"
                      type="warning"
                    />
                    <ValuePoint
                      text="Deducting Medicare Part B Premium"
                      type="success"
                    />
                  </>
                ) : (
                  <ValuePoint
                    text="Not Deducting Medicare Part B Premium"
                    type="error"
                  />
                )}
                {dateToYears(intakeState.dateOfBirth, new Date()).years >=
                  64 && (
                  <ValuePoint text="Strategy - Discuss Subsidy Continuing into Retirement" />
                )}
                {suggestedPremium?.name.includes('Blue Cross') && (
                  <ValuePoint text="Strategy - Discuss Basic vs Standard & Part B" />
                )}
                {healthInsurance === 'pshb' && (
                  <ValuePoint text="Strategy - discuss Medicare Part B Enrollment Requirements" />
                )}
                {maritalStatus === 'married' && (
                  <ValuePoint text="Strategy - discuss FEHB contingency" />
                )}
                {fehbReimbursement(
                  intakeState?.familyMaritalStatus,
                  intakeState?.familyDependents
                    ? intakeState?.familyDependents
                    : [],
                  suggestedPremium?.type
                ) && (
                  <ValuePoint text="Pursue FEHB Reimbursement" type="warning" />
                )}
                {yearsUntil26 && yearsUntil26 < 10 && (
                  <ValuePoint
                    text={`Youngest turns 26 in ${yearsUntil26} years and will not be eliegble to stay on heatlhcare at that time`}
                  />
                )}
              </Collapse>
            </Grid>
          </Grid>
        </Card>
      </Grid>
      <Grid item xs={12} md={8} xl={9}>
        <Card
          id="healthcare"
          sx={{
            overflow: 'visible',
            textAlign: 'left',
            boxShadow: '0px 4px 25px -8px rgba(0,0,0,0.2)',
            borderRadius: 2,
            p: 2,
            pb: 0,
          }}
        >
          <Grid container columnSpacing={3} rowSpacing={2} pb={2}>
            <Grid
              item
              xs={12}
              onClick={() => toggleCollapse('healthcare')}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography variant="h4" sx={{ flexGrow: 1 }}>
                Healthcare
              </Typography>
              <ExpandMore
                sx={{
                  height: '1.5em',
                  width: '1.5em',
                  transform: collapseSections.healthcare
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                  transition: 'transform 0.3s ease-in-out',
                }}
              />
            </Grid>
          </Grid>
          <Collapse in={collapseSections.healthcare} style={{ width: '100%' }}>
            <Grid container columnSpacing={3} rowSpacing={2} pb={2}>
              {isGovernmentEmployee && (
                <>
                  <Grid item xs={12} md={4}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'healthInsurance',
                      label: 'Health insurance program',
                      placeholder: '',
                      inputType: 'select',
                      value: intakeState.healthInsurance,
                      onChange: updateIntakeStateAndSubmit((healthInsurance) =>
                        updateIntakeStateAtKey({ healthInsurance })
                      ),
                      options: selectData.healthInsurance,
                    })}
                  </Grid>

                  {healthInsurance === 'fehb' || healthInsurance === 'pshb' ? (
                    <Grid item xs={12} md={8}>
                      {renderMuiFormField({
                        autoFocus: false,
                        id: 'fehbCode',
                        label: `Select Your ${intakeState?.healthInsurance.toUpperCase()} Code`,
                        placeholder: '',
                        inputType: 'select',
                        searchable: true,
                        value: intakeState.fehbCode,
                        onChange: updateIntakeStateAndSubmit((fehbCode) =>
                          updateIntakeStateAtKey({ fehbCode })
                        ),
                        options:
                          healthInsurance === 'fehb'
                            ? selectData.fehbCode.slice(0, 438)
                            : selectData.fehbCode.slice(-234),
                      })}
                      {intakeState?.fehbCode && (
                        <Typography variant="inputNote" component="p">
                          Plan Type: {suggestedPremium?.type}
                        </Typography>
                      )}
                    </Grid>
                  ) : (
                    <></>
                  )}
                </>
              )}

              <Grid item xs={12} md={6}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'healthInsurancePremium',
                  label: 'What is your healthcare premium per pay period?',
                  placeholder: `${
                    (healthInsurance === 'fehb' ||
                      healthInsurance === 'pshb') &&
                    intakeState?.fehbCode
                      ? '$' + suggestedPremium?.biweeklyEmp
                      : '$275.00'
                  }`,
                  inputType: 'currencyFloat',
                  value: localForm.healthInsurancePremium,
                  onChange: (e) => handleChange('healthInsurancePremium')(e),
                  onBlur: updateIntakeStateAndSubmit((healthInsurancePremium) =>
                    updateIntakeStateAtKey({ healthInsurancePremium })
                  ),
                })}
                {(healthInsurance === 'fehb' || healthInsurance === 'pshb') &&
                intakeState?.fehbCode ? (
                  <>
                    <Typography variant="inputNote" component="p">
                      Suggested Premium Based on Code: $
                      {suggestedPremium?.biweeklyEmp}
                    </Typography>
                    <Typography variant="inputNote" component="p">
                      Suggested MONTHLY Premium Based on Code:{' '}
                      {dollarCentsFormat.format(
                        (suggestedPremium?.biweeklyEmp * 26) / 12
                      )}
                    </Typography>
                    <Button
                      variant="parcoRounded"
                      href={`https://www.opm.gov/healthcare-insurance/healthcare/plan-information/compare-plans/fehb/Plans?FFSSearch=on&Medicare=False&ZipCode=${intakeState?.zipcode}&IncludeNationwide=True&empType=b&payPeriod=a`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ marginTop: '8px' }}
                    >
                      Find FEHB Plan
                    </Button>
                  </>
                ) : (
                  <></>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'healthInsurancePremiumRetirement',
                  label: 'Healthcare Cost in Retirement (Monthly)',
                  placeholder: `Suggested ${dollarCentsFormat.format(
                    (suggestedPremium?.biweeklyEmp * 26) / 12
                  )}`,
                  inputType: 'currencyFloat',
                  value: localForm.healthInsurancePremiumRetirement,
                  onChange: (e) =>
                    handleChange('healthInsurancePremiumRetirement')(e),
                  onBlur: updateIntakeStateAndSubmit(
                    (healthInsurancePremiumRetirement) =>
                      updateIntakeStateAtKey({
                        healthInsurancePremiumRetirement,
                      })
                  ),
                })}
              </Grid>
            </Grid>
          </Collapse>
        </Card>
      </Grid>
    </>
  )
}

export default Healthcare
